import { isTimeOffProjectCategory } from 'utils/projectCategories';

// How a time entry relates to work-order reporting (INM-129): LINKED has an
// order, NOT_REQUIRED is exempt (controlled absence or no client), MISSING
// needs an order but has none, NOT_IN_SCOPE is outside the rollout.
export type TimeEntryOrderStatus =
  | 'LINKED'
  | 'NOT_REQUIRED'
  | 'MISSING'
  | 'NOT_IN_SCOPE';

// The assignment of an Assignment allocations row and of a Reports > Time
// reports row (owner, 24.10, D10):
// linked hours show their number; unlinked ones read "No assignment needed"
// (not in scope or not required) or "Missing assignment", which needs
// follow-up with the issuer. Without a status (older backends) they stay
// "Historical — no assignment".
export type AllocationOrderStatus = 'NOT_NEEDED' | 'MISSING';

export const getAllocationOrderLabel = ({
  orderId,
  orderStatus,
}: {
  orderId: number | null;
  orderStatus?: AllocationOrderStatus | null;
}) => {
  if (orderId != null) {
    return null;
  }
  if (orderStatus === 'MISSING') {
    return 'ORDER_PRESENTATION.MISSING_ASSIGNMENT';
  }
  if (orderStatus === 'NOT_NEEDED') {
    return 'ORDER_PRESENTATION.NO_ASSIGNMENT_NEEDED';
  }
  return 'ORDER_PRESENTATION.HISTORICAL';
};

export interface OrderAllocation {
  orderId?: number | null;
  orderNumber?: string | null;
  orderHours?: number | null;
  orderDays?: number | null;
  orderStatus?: TimeEntryOrderStatus | null;
  needsCorrection?: boolean | null;
}

export type OrderCellState =
  | { kind: 'pending'; orderNumber?: string }
  | { kind: 'linked'; orderNumber: string }
  | { kind: 'notRequired' }
  | { kind: 'missing' }
  | { kind: 'notInScope' }
  | { kind: 'historical' };

// Rows expose the internal orderId for allocation state and the
// user-facing orderNumber for display. An unsaved change is pending. Without
// orderStatus (older backends) an undefined orderId means the order is not
// resolved yet and null means a historical record.
// pendingOrderNumber: the assignment a save would link, shown while the
// row awaits its save (owner, 24.10, U10).
export const getOrderCellState = (
  { orderId, orderNumber, orderStatus }: OrderAllocation,
  pending = false,
  // Custom-project rows are never saved from this view, so a missing order
  // there is historical rather than awaiting a save.
  unresolvedIsPending = true,
  pendingOrderNumber?: string | null,
): OrderCellState => {
  const pendingState: OrderCellState = pendingOrderNumber
    ? { kind: 'pending', orderNumber: pendingOrderNumber }
    : { kind: 'pending' };
  if (pending) {
    return pendingState;
  }
  switch (orderStatus) {
    case 'LINKED':
      return { kind: 'linked', orderNumber: orderNumber || '—' };
    case 'NOT_REQUIRED':
      return { kind: 'notRequired' };
    case 'MISSING':
      return { kind: 'missing' };
    case 'NOT_IN_SCOPE':
      return { kind: 'notInScope' };
    default:
      break;
  }
  if (orderId === undefined && unresolvedIsPending) {
    return pendingState;
  }
  if (orderId === null || orderId === undefined) {
    return { kind: 'historical' };
  }
  return { kind: 'linked', orderNumber: orderNumber || '—' };
};

// Dates (YYYY-MM-DD) of entries linked to an order that ended or was
// cancelled before them; they block approval until corrected.
export const getCorrectionDates = (
  entries: Array<{ date?: string | null; needsCorrection?: boolean | null }>,
) =>
  Array.from(
    new Set(
      entries
        .filter(({ needsCorrection, date }) => needsCorrection && date)
        .map(({ date }) => String(date).slice(0, 10)),
    ),
  ).sort();

// Everything that blocks approval: the days above plus the monthly
// custom-project entries (they carry no date, so they are named by project).
export const getCorrectionEntries = (
  days: Array<{ date?: string | null; needsCorrection?: boolean | null }>,
  customEntries: Array<{
    projectName?: string | null;
    needsCorrection?: boolean | null;
  }> = [],
) => ({
  dates: getCorrectionDates(days),
  projects: Array.from(
    new Set(
      customEntries
        .filter(({ needsCorrection }) => needsCorrection)
        .map(({ projectName }) => projectName || '—'),
    ),
  ),
});

interface MultiplierSource {
  projectBasicInfo?: {
    multiplicatorValue?: number | string | null;
    projectCategory?: string | null;
  } | null;
}

const isNonStandard = (value?: number | string | null) =>
  value !== null && value !== undefined && value !== '' && Number(value) !== 1;

// The multiplier column is shown only when a work entry, or a work project
// the user can report on, differs from 100%. Absences are ignored, and so are
// inactive projects, which cannot be picked (saved entries on them still
// count; change request of 15.10, item 3).
// Whether each date of the report is in assignment scope (orderInScope of
// its day rows; split rows share their date's value). Dates without the
// field are left out, so an older backend yields an empty map.
export const getOrderScopeByDate = (
  days?: Array<{ date?: string | null; orderInScope?: boolean | null }>,
) =>
  (days || []).reduce((scope, { date, orderInScope }) => {
    if (date && typeof orderInScope === 'boolean') {
      const key = date.slice(0, 10);
      scope.set(key, Boolean(scope.get(key)) || orderInScope);
    }
    return scope;
  }, new Map<string, boolean>());

export const hasNonStandardMultiplier = (
  days?: MultiplierSource[] | null,
  projects?: Array<{
    multiplicator?: number | string | null;
    reportingCustomData?: boolean;
    projectCategory?: string | null;
    status?: string | null;
  }> | null,
) =>
  (days || []).some(
    ({ projectBasicInfo }) =>
      !isTimeOffProjectCategory(projectBasicInfo?.projectCategory) &&
      isNonStandard(projectBasicInfo?.multiplicatorValue),
  ) ||
  (projects || []).some(
    ({ multiplicator, reportingCustomData, projectCategory, status }) =>
      !reportingCustomData &&
      status !== 'INACTIVE' &&
      !isTimeOffProjectCategory(projectCategory) &&
      isNonStandard(multiplicator),
  );

export const clearOrderAllocation = <T extends Record<string, unknown>>(
  entry: T,
) =>
  ({
    ...entry,
    orderId: undefined,
    orderNumber: undefined,
    orderHours: undefined,
    orderDays: undefined,
    orderStatus: undefined,
    needsCorrection: undefined,
  } as T);
